import {
    type RevisionPage,
    type RevisionPageComputed,
    type RevisionPageDocument,
    type RevisionPageGroup,
    type RevisionPageLink,
    type RevisionPageParent,
    RevisionPageType,
} from '../../types';
import { mutations } from '../../utils';

/**
 * Determines whether the given RevisionPage is a RevisionPageDocument
 */
export function isRevisionPageDocument(
    revisionPage: RevisionPage
): revisionPage is RevisionPageDocument;
export function isRevisionPageDocument(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageDocument>;
export function isRevisionPageDocument(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageDocument> {
    return mutations.get(revisionPage, 'type') === RevisionPageType.Document;
}

/**
 * Determines whether the given RevisionPage is a RevisionPageGroup
 */
export function isRevisionPageGroup(revisionPage: RevisionPage): revisionPage is RevisionPageGroup;
export function isRevisionPageGroup(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageGroup>;
export function isRevisionPageGroup(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageGroup> {
    return mutations.get(revisionPage, 'type') === RevisionPageType.Group;
}

/**
 * Determines whether the given RevisionPage is a RevisionPageLink
 */
export function isRevisionPageLink(revisionPage: RevisionPage): revisionPage is RevisionPageLink;
export function isRevisionPageLink(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageLink>;
export function isRevisionPageLink(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageLink> {
    return mutations.get(revisionPage, 'type') === RevisionPageType.Link;
}

/**
 * Determines whether the given RevisionPage is a RevisionPageComputed
 */
export function isRevisionPageComputed(
    revisionPage: RevisionPage
): revisionPage is RevisionPageComputed;
export function isRevisionPageComputed(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageComputed>;
export function isRevisionPageComputed(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageComputed> {
    return mutations.get(revisionPage, 'type') === RevisionPageType.Computed;
}

/**
 * Determines whether the given RevisionPage is a RevisionPageGroup or RevisionPageDocument
 */
export function isRevisionPageParent(
    revisionPage: RevisionPage
): revisionPage is RevisionPageParent;
export function isRevisionPageParent(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageParent>;
export function isRevisionPageParent(
    revisionPage: mutations.MutableData<RevisionPage>
): revisionPage is mutations.MutableData<RevisionPageParent> {
    const type = mutations.get(revisionPage, 'type');
    return type === RevisionPageType.Group || type === RevisionPageType.Document;
}

/**
 * Asserts whether the given RevisionPage is a RevisionPageDocument
 */
export function assertRevisionPageDocument(
    revisionPage: RevisionPage
): asserts revisionPage is RevisionPageDocument;
export function assertRevisionPageDocument(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageDocument;
export function assertRevisionPageDocument(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageDocument {
    if (!isRevisionPageDocument(revisionPage)) {
        throw new Error('expected RevisionPageDocument');
    }
}
/**
 * Asserts whether the given RevisionPage is a RevisionPageGroup
 */
export function assertRevisionPageGroup(
    revisionPage: RevisionPage
): asserts revisionPage is RevisionPageGroup;
export function assertRevisionPageGroup(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageGroup;
export function assertRevisionPageGroup(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageGroup {
    if (!isRevisionPageGroup(revisionPage)) {
        throw new Error('expected RevisionPageGroup');
    }
}
/**
 * Asserts whether the given RevisionPage is a RevisionPageLink
 */
export function assertRevisionPageLink(
    revisionPage: RevisionPage
): asserts revisionPage is RevisionPageLink;
export function assertRevisionPageLink(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageLink;
export function assertRevisionPageLink(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageLink {
    if (!isRevisionPageLink(revisionPage)) {
        throw new Error('expected RevisionPageLink');
    }
}
/**
 * Asserts whether the given RevisionPage is a RevisionPageParent
 */
export function assertRevisionPageParent(
    revisionPage: RevisionPage
): asserts revisionPage is RevisionPageParent;
export function assertRevisionPageParent(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageParent;
export function assertRevisionPageParent(
    revisionPage: mutations.MutableData<RevisionPage>
): asserts revisionPage is RevisionPageParent {
    if (!isRevisionPageParent(revisionPage)) {
        throw new Error('expected RevisionPageParent');
    }
}
